import { z } from 'zod'
import { Timestamp } from './common.js'

// Route points — first-class editable product points inside route geometry.
//
// Storage model: the canonical GPX blob (`canonical-v1/routes/<id>/geometry.gpx`)
// remains source of truth. Backend parses `<wpt>` nodes on read, mutates and
// re-serialises on write. Stable per-waypoint identity is persisted in the GPX
// itself via the `xr:id` extension namespace so that round-trips through third-
// party tools preserve identity when possible. Clients never see a separate
// Mongo document for waypoints.

export const RoutePointKind = z.enum([
  // Universal
  'generic',
  'viewpoint',
  'water_source',
  'campsite',
  'hazard',
  'rest_stop',
  'resupply',
  'parking',
  // Hiking-leaning
  'trailhead',
  'junction',
  'shelter',
])
export type RoutePointKind = z.infer<typeof RoutePointKind>
export const WaypointKind = RoutePointKind
export type WaypointKind = z.infer<typeof WaypointKind>

export const RoutePoint = z.object({
  /** Stable UUID; emitted as <wpt><extensions><xr:id> in the GPX. */
  id: z.string().uuid(),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  ele: z.number().optional(),
  name: z.string().max(200).optional(),
  desc: z.string().max(2000).optional(),
  name_en: z.string().min(1).max(120).optional(),
  desc_en: z.string().max(1000).optional(),
  /** Short freeform note (field observation / editing remark). */
  note: z.string().max(500).optional(),
  /** When this waypoint was captured during a track recording. */
  captured_at: Timestamp.optional(),
  /** Cumulative distance from route line start, in kilometres, for generated points. */
  distance_along_line: z.number().nonnegative().optional(),
  /** Legacy GPX extension alias for distance_along_line during migration. */
  distance_along_track: z.number().nonnegative().optional(),
  /** @deprecated Formerly marked backend-generated waypoints. No longer written by new code; retained for reading old data. */
  auto: z.boolean().default(false),
  /** GPX <sym> tag — Garmin-style waypoint symbol name. */
  sym: z.string().max(64).optional(),
  /** XingRoute-native waypoint kind — takes display priority over sym. */
  kind: RoutePointKind.optional(),
})
export type RoutePoint = z.input<typeof RoutePoint>
export const Waypoint = RoutePoint
export type Waypoint = z.input<typeof Waypoint>

export const RoutePointPublic = RoutePoint
export type RoutePointPublic = RoutePoint
export const WaypointPublic = RoutePointPublic
export type WaypointPublic = Waypoint

export const RoutePointInput = RoutePoint.omit({
  auto: true,
  distance_along_line: true,
  distance_along_track: true,
})
  .extend({
    id: z.string().uuid().optional(),
  })
  .strict()
export type RoutePointInput = z.input<typeof RoutePointInput>

// Lightweight route line point projection returned so the frontend can render
// the public route line without re-fetching the raw geometry blob.
export const RouteLinePoint = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  ele: z.number().optional(),
  time: Timestamp.optional().describe('Source GPX point time; not a route duration estimate.'),
})
export type RouteLinePoint = z.infer<typeof RouteLinePoint>
export const TrackPoint = RouteLinePoint
export type TrackPoint = z.infer<typeof TrackPoint>

export const GeometryResponse = z.object({
  /** Canonical route ETag; identical to the one served by GET /routes/:id. */
  etag: z.string(),
  geometry_type: z.literal('line'),
  line: z.array(RouteLinePoint),
  points: z.array(RoutePointPublic),
  /** Legacy alias during the line/points migration. New code should use line. */
  track: z.array(RouteLinePoint).optional(),
  /** Legacy alias during the line/points migration. New code should use points. */
  waypoints: z.array(RoutePointPublic).optional(),
})
export type GeometryResponse = z.input<typeof GeometryResponse>

// Request body for PUT /routes/:id/waypoints — full replacement, batched commit.
// Server assigns ids to any waypoint whose `id` is omitted (drag-to-add).
export const WaypointsReplaceRequest = z.object({
  waypoints: z
    .array(
      Waypoint.extend({
        id: z.string().uuid().optional(),
      }),
    )
    .max(100),
})
export type WaypointsReplaceRequest = z.input<typeof WaypointsReplaceRequest>
